/**
 * BoardDashboardPage — 워크스페이스 게시판 활동 허브
 *
 * 좌측 메뉴에서 "게시판" 클릭(또는 /board) 으로 진입.
 * 헤더에 통합 검색, 본문에 최근 게시물(좌) + 우측 사이드(나에게 / 최근 댓글) + 핀 게시판 + 이슈/Q&A 요약.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Search, X, MessageSquare, AtSign, Reply, FileText, Bell, BookOpen, ListTodo, HelpCircle, Hash,
  Loader2, ChevronDown, AlertTriangle, Users, BellDot, Star,
} from 'lucide-react';
import { Trans, t } from '@lingui/macro';
import { useAppUIContext } from '../../context/AppUIContext';
import { useBoardContext } from '../../context/BoardContext';
import { useHistory } from '../../hooks/useRouterCompat';
import { My } from '../../services/My';
import {
  fetchRecentPostsForWorkspace,
  fetchRecentCommentsForWorkspace,
  fetchMyFeed,
  fetchParticipatingComments,
  searchAcrossWorkspace,
  DashboardPostItem,
  DashboardCommentItem,
  MyFeedItem,
  ParticipatingCommentItem,
  WorkspaceSearchResults,
  SearchFields,
  DEFAULT_SEARCH_FIELDS,
} from '../../services/BoardService';
import { Post, BoardType, getIssueMetadata } from '../../models/Board';
import { cn } from '../../lib/utils';
import { UserAvatar } from '../components/UserAvatar';
import { timeAgo, NotifDotFor } from './renderers/shared';
import { MenuToggleButton } from '../components/MenuToggleButton';
import { buildBoardUrl } from './boardUrl';
import BoardNotifyAllSettingsModal from './BoardNotifyAllSettingsModal';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuCheckboxItem,
} from '../../components/ui/dropdown-menu';

function boardIconOf(type: string): React.ElementType {
  switch (type) {
    case 'notice': return Bell;
    case 'qna':    return HelpCircle;
    case 'wiki':   return BookOpen;
    case 'issue':  return ListTodo;
    default:       return FileText;
  }
}

interface BoardDashboardPageProps {
  /** 채널 인라인 모드 — 데이터를 scopeBoardIds 로 한정, 내피드/참여 섹션·자체 헤더 숨김 */
  channelMode?: boolean;
  /** channelMode 일 때 대시보드 데이터를 한정할 게시판 ID 목록 */
  scopeBoardIds?: string[];
  /** channelMode 라우팅 — 게시글 열기 (history 대신 채널 인라인 상태 전환) */
  onNavigatePost?: (boardId: string, postId: string) => void;
  /** channelMode 라우팅 — 게시판(글 목록) 열기 */
  onNavigateBoard?: (boardId: string) => void;
}

const BoardDashboardPage: React.FC<BoardDashboardPageProps> = ({
  channelMode = false,
  scopeBoardIds,
  onNavigatePost,
  onNavigateBoard,
}) => {
  const { activeWorkspaceId } = useAppUIContext();
  // pinnedIds 는 BoardContext 공유 — 좌측 메뉴에서 토글 시 실시간 반영.
  const { boards, posts: ctxPosts, setSelectedPost, setSelectedBoard, pinnedIds } = useBoardContext();
  const history = useHistory();

  // 데이터 — 섹션별 limit 은 localStorage 에 저장해 다음 방문에 유지.
  // 'posts' 는 페이지네이션이라 더보기 클릭마다 누적; 다른 섹션은 한 번에 fetch.
  const [postsLimit, setPostsLimit] = useLocalStorageNumber('board-dashboard-limit:posts', 20);
  const [commentsLimit, setCommentsLimit] = useLocalStorageNumber('board-dashboard-limit:comments', 20);
  const [mentionsLimit, setMentionsLimit] = useLocalStorageNumber('board-dashboard-limit:mentions', 20);
  const [participatingLimit, setParticipatingLimit] = useLocalStorageNumber('board-dashboard-limit:participating', 20);

  const [recentPosts, setRecentPosts] = useState<DashboardPostItem[]>([]);
  const [postsHasMore, setPostsHasMore] = useState(true);
  const [postsLoadingMore, setPostsLoadingMore] = useState(false);
  const [recentComments, setRecentComments] = useState<DashboardCommentItem[]>([]);
  const [myFeed, setMyFeed] = useState<MyFeedItem[]>([]);
  const [participatingComments, setParticipatingComments] = useState<ParticipatingCommentItem[]>([]);
  const [loading, setLoading] = useState(false);

  // 좌측 메인 탭: 게시물 / 댓글 (즐겨찾기 토글이 양쪽에 적용)
  const [leftTab, setLeftTab] = useState<'posts' | 'comments'>('posts');
  // 즐겨찾기 보드 필터 토글 — 게시물·댓글 양쪽 공통
  const [pinnedOnly, setPinnedOnly] = useState(false);

  // 통합 검색 + 필터 (다중 선택)
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFields, setSearchFields] = useState<SearchFields>(DEFAULT_SEARCH_FIELDS);
  const [searchResults, setSearchResults] = useState<WorkspaceSearchResults | null>(null);
  const [searching, setSearching] = useState(false);
  const toggleSearchField = (k: keyof SearchFields) => {
    setSearchFields(prev => {
      const next = { ...prev, [k]: !prev[k] };
      // 최소 하나는 켜져 있어야 함 — 마지막 하나를 끄려고 하면 원상복구
      if (!next.title && !next.body && !next.comments && !next.author) return prev;
      return next;
    });
  };
  // 검색 종류 dropdown 표시 — 1개는 라벨, 2개+ 는 "라벨 +N" 으로 간결 표시
  const SEARCH_FIELD_DEFS: { k: keyof SearchFields; label: string }[] = [
    { k: 'title', label: t`Title` },
    { k: 'body', label: t`Body` },
    { k: 'comments', label: t`Comments` },
    { k: 'author', label: t`Author` },
  ];
  const selectedSearchDefs = SEARCH_FIELD_DEFS.filter(d => searchFields[d.k]);
  const searchTypeLabel = selectedSearchDefs.length <= 1
    ? (selectedSearchDefs[0]?.label ?? t`Type`)
    : `${selectedSearchDefs[0].label} +${selectedSearchDefs.length - 1}`;

  // 우측 컬럼 탭 — 나에게 / 참여중 (댓글 탭은 좌측 메인으로 이동)
  const [rightTab, setRightTab] = useState<'me' | 'active'>('me');

  // 통합 알림 설정 모달
  const [showNotifyAll, setShowNotifyAll] = useState(false);

  const userId = My.user?.id;

  // ── 이슈 요약 (Q&A 칩은 제거 — 검색폼이 그 자리 차지) ──
  const stats = useMemo(() => {
    // 워크스페이스 대시보드: 워크스페이스 스코프 이슈 게시판만 (채널 게시판 제외)
    const issueBoards = boards.filter(b => b.type === 'issue' && (channelMode || b.scope === 'workspace'));
    let openIssues = 0;
    for (const post of ctxPosts) {
      const board = boards.find(b => b.id === post.board_id);
      if (!board) continue;
      if (!channelMode && board.scope !== 'workspace') continue;
      if (board.type === 'issue') {
        const m = getIssueMetadata(post);
        if (m && m.status !== 'done') openIssues++;
      }
    }
    return { hasIssue: issueBoards.length > 0, openIssues };
  }, [boards, ctxPosts]);

  // ── 데이터 로드 ──
  // 나에게 — workspace/user + mentionsLimit
  useEffect(() => {
    if (!activeWorkspaceId) return;
    if (channelMode) { setMyFeed([]); setLoading(false); return; }  // 채널 모드: 워크스페이스 전역 섹션 미사용
    setLoading(true);
    (userId
      ? fetchMyFeed(activeWorkspaceId, userId, { limit: mentionsLimit })
      : Promise.resolve([] as MyFeedItem[]))
      .then(setMyFeed)
      .catch(err => console.error('[BoardDashboard] 멘션 로드 실패:', err))
      .finally(() => setLoading(false));
  }, [activeWorkspaceId, userId, mentionsLimit, channelMode]);

  // 참여중 댓글 — workspace/user + participatingLimit
  useEffect(() => {
    if (!activeWorkspaceId) return;
    if (channelMode) { setParticipatingComments([]); return; }  // 채널 모드: 미사용
    (userId
      ? fetchParticipatingComments(activeWorkspaceId, userId, { limit: participatingLimit })
      : Promise.resolve([] as ParticipatingCommentItem[]))
      .then(setParticipatingComments)
      .catch(err => console.error('[BoardDashboard] 참여중 로드 실패:', err));
  }, [activeWorkspaceId, userId, participatingLimit, channelMode]);

  // 최근 게시물 (pinnedOnly 토글 + postsLimit + 워크스페이스 의존)
  useEffect(() => {
    if (!activeWorkspaceId) return;
    fetchRecentPostsForWorkspace(activeWorkspaceId, {
      limit: postsLimit,
      offset: 0,
      boardIds: channelMode ? scopeBoardIds : (pinnedOnly ? pinnedIds : undefined),
      workspaceScopeOnly: !channelMode,
    })
      .then(p => {
        setRecentPosts(p);
        setPostsHasMore(p.length === postsLimit);
      })
      .catch(err => console.error('[BoardDashboard] 최근 게시물 로드 실패:', err));
  }, [activeWorkspaceId, pinnedOnly, pinnedIds, postsLimit, channelMode, scopeBoardIds]);

  // 최근 댓글 (pinnedOnly 토글 + commentsLimit + 워크스페이스 의존)
  useEffect(() => {
    if (!activeWorkspaceId) return;
    fetchRecentCommentsForWorkspace(activeWorkspaceId, {
      limit: commentsLimit,
      boardIds: channelMode ? scopeBoardIds : (pinnedOnly ? pinnedIds : undefined),
      workspaceScopeOnly: !channelMode,
    })
      .then(setRecentComments)
      .catch(err => console.error('[BoardDashboard] 최근 댓글 로드 실패:', err));
  }, [activeWorkspaceId, pinnedOnly, pinnedIds, commentsLimit, channelMode, scopeBoardIds]);

  // 최근 게시물 더보기 — postsLimit 만큼 추가 fetch
  const loadMorePosts = async () => {
    if (!activeWorkspaceId || postsLoadingMore || !postsHasMore) return;
    setPostsLoadingMore(true);
    try {
      const more = await fetchRecentPostsForWorkspace(activeWorkspaceId, {
        limit: postsLimit,
        offset: recentPosts.length,
        boardIds: channelMode ? scopeBoardIds : (pinnedOnly ? pinnedIds : undefined),
        workspaceScopeOnly: !channelMode,
      });
      setRecentPosts(prev => [...prev, ...more]);
      setPostsHasMore(more.length === postsLimit);
    } catch (err) {
      console.error('[BoardDashboard] 추가 로드 실패:', err);
    } finally {
      setPostsLoadingMore(false);
    }
  };

  // ── 통합 검색 (debounce) — 게시글 + 댓글, fields 다중 필터 적용 ──
  useEffect(() => {
    if (!activeWorkspaceId) return;
    const q = searchQuery.trim();
    if (!q) { setSearchResults(null); return; }
    setSearching(true);
    const timer = setTimeout(() => {
      searchAcrossWorkspace(activeWorkspaceId, q, { limit: 20, fields: searchFields, boardIds: channelMode ? scopeBoardIds : undefined, workspaceScopeOnly: !channelMode })
        .then(setSearchResults)
        .catch(err => {
          console.error('[BoardDashboard] 검색 실패:', err);
          setSearchResults({ posts: [], comments: [] });
        })
        .finally(() => setSearching(false));
    }, 250);
    return () => { clearTimeout(timer); setSearching(false); };
  }, [searchQuery, activeWorkspaceId, searchFields, channelMode, scopeBoardIds]);

  // ── 클릭 핸들러 ──
  // history state.fromDashboard=true → PostDetailOverlay 의 '← 목록' 버튼이 '← 대시보드' 로 변경되어 /board 로 복귀.
  const goToPost = (item: DashboardPostItem) => {
    if (channelMode) { onNavigatePost?.(item.boardId, item.post.id); return; }
    const board = boards.find(b => b.id === item.boardId);
    if (board) setSelectedBoard(board);
    history.push(buildBoardUrl(item.boardId, item.post.id), { fromDashboard: true });
  };
  const goToComment = (item: DashboardCommentItem) => {
    if (channelMode) { onNavigatePost?.(item.boardId, item.postId); return; }
    const board = boards.find(b => b.id === item.boardId);
    if (board) setSelectedBoard(board);
    history.push(buildBoardUrl(item.boardId, item.postId), { fromDashboard: true });
  };
  const goToBoard = (boardId: string) => {
    if (channelMode) { onNavigateBoard?.(boardId); return; }
    history.push(buildBoardUrl(boardId));
  };

  return (
    <div className="flex flex-col h-full bg-background rounded-panel-mobile lg:rounded-panel overflow-hidden">
      {/* ── 헤더 — 모바일 메뉴 토글 + 타이틀 + 내 알림 설정 (채널 모드에서는 채널 헤더가 있으므로 숨김) ── */}
      {!channelMode && (
      <header className="flex items-center gap-2.5 px-4 h-14 shrink-0 border-b border-border">
        <MenuToggleButton />
        <FileText className="size-5 text-primary" strokeWidth={2} />
        <h1 className="text-body-lg font-semibold text-foreground"><Trans>Boards</Trans></h1>
        <button
          type="button"
          onClick={() => setShowNotifyAll(true)}
          className="ml-auto size-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-muted transition-colors cursor-pointer"
          title={t`My notification settings`}
          aria-label={t`My notification settings`}
        >
          <BellDot className="size-4" />
        </button>
      </header>
      )}

      {/* ── 콘텐츠 ── */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="max-w-[1200px] mx-auto px-5 py-5 space-y-5">

          {/* ── 통합 검색 (헤더에서 내려옴, Q&A chip 자리) ── */}
          <div className="relative">
            <div className="flex items-center gap-2">
              <div className="relative flex-1 min-w-0 sm:min-w-[260px] max-w-[520px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder={channelMode ? t`Search this channel…` : t`Search all boards…`}
                  className="w-full h-10 pl-9 pr-9 text-sm bg-muted/40 hover:bg-muted/60 focus:bg-background border border-border rounded-lg outline-none transition-colors focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/10"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 size-6 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer"
                    aria-label={t`Clear search`}
                  >
                    <X className="size-3.5" />
                  </button>
                )}
              </div>

              {/* 검색 종류 — 다중 선택 dropdown (2개+ 는 "+N" 으로 간결 표시, 최소 하나 유지) */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-meta-sm font-medium border border-border bg-card text-foreground hover:bg-muted transition-colors shrink-0"
                  >
                    <span>{searchTypeLabel}</span>
                    <ChevronDown className="size-3 text-muted-foreground shrink-0" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-40">
                  {SEARCH_FIELD_DEFS.map(d => (
                    <DropdownMenuCheckboxItem
                      key={d.k}
                      checked={!!searchFields[d.k]}
                      onCheckedChange={() => toggleSearchField(d.k)}
                      onSelect={e => e.preventDefault()}
                    >
                      {d.label}
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

            </div>

            {/* 검색 결과 dropdown — 게시글 + 댓글 두 섹션 */}
            {searchResults !== null && searchQuery.trim() && (
              <div className="absolute left-0 right-0 top-full mt-1.5 max-h-[60vh] overflow-y-auto rounded-xl border border-border bg-background shadow-lg z-50 py-1">
                {searching ? (
                  <div className="flex items-center justify-center py-6 text-muted-foreground">
                    <Loader2 className="size-4 animate-spin" />
                  </div>
                ) : (searchResults.posts.length === 0 && searchResults.comments.length === 0) ? (
                  <p className="px-4 py-6 text-meta-sm text-muted-foreground text-center">
                    <Trans>No search results</Trans>
                  </p>
                ) : (
                  <>
                    {searchResults.posts.length > 0 && (
                      <>
                        <div className="flex items-center gap-1.5 px-3 py-1.5 text-meta-sm font-semibold text-muted-foreground uppercase tracking-wider border-b border-border mb-1">
                          <FileText className="size-3" />
                          <Trans>Posts ({searchResults.posts.length})</Trans>
                        </div>
                        {searchResults.posts.map(r => (
                          <button
                            key={r.post.id}
                            onClick={() => { goToPost(r); setSearchQuery(''); }}
                            className="w-full text-left px-3 py-2 hover:bg-muted/60 cursor-pointer transition-colors"
                          >
                            <div className="flex items-center gap-2 text-meta-sm text-muted-foreground mb-0.5">
                              {(() => { const I = boardIconOf(r.boardType); return <I className="size-3 shrink-0" />; })()}
                              <span className="truncate">{r.boardName}</span>
                              <span>·</span>
                              <span className="truncate">{r.post.author.name}</span>
                            </div>
                            <p className="text-label font-medium text-foreground truncate flex items-center gap-1.5"><span className="truncate">{r.post.title}</span><NotifDotFor postId={r.post.id} /></p>
                          </button>
                        ))}
                      </>
                    )}
                    {searchResults.comments.length > 0 && (
                      <>
                        <div className="flex items-center gap-1.5 px-3 py-1.5 mt-1 text-meta-sm font-semibold text-muted-foreground uppercase tracking-wider border-b border-t border-border mb-1">
                          <MessageSquare className="size-3" />
                          <Trans>Comments ({searchResults.comments.length})</Trans>
                        </div>
                        {searchResults.comments.map(r => (
                          <button
                            key={r.comment.id}
                            onClick={() => { goToComment(r); setSearchQuery(''); }}
                            className="w-full text-left px-3 py-2 hover:bg-muted/60 cursor-pointer transition-colors"
                          >
                            <div className="flex items-center gap-2 text-meta-sm text-muted-foreground mb-0.5">
                              <span className="truncate">{r.comment.author.name}</span>
                              <span>·</span>
                              <span className="truncate">{r.boardName}</span>
                            </div>
                            <p className="text-meta text-foreground/90 line-clamp-2 leading-snug">{r.comment.text}</p>
                            <p className="text-meta-sm text-muted-foreground truncate mt-0.5">↳ {r.postTitle}</p>
                          </button>
                        ))}
                      </>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* 진행중 이슈 (Q&A chip 은 검색폼이 그 자리 차지, 즐겨찾기 chip 은 좌측 메뉴와 중복되어 제거) */}
          {stats.hasIssue && (
            <div className="flex flex-wrap items-center gap-2">
              <div className="inline-flex items-center gap-1.5 px-2.5 h-7 rounded-full bg-badge-caution-bg text-badge-caution text-meta-sm font-medium border border-badge-caution/20">
                <AlertTriangle className="size-3" />
                <Trans>Open issues {stats.openIssues}</Trans>
              </div>
            </div>
          )}

          {/* 본문 grid — 채널 모드는 우측 컬럼(내피드/참여) 없으므로 단일 컬럼 */}
          <div className={cn('grid grid-cols-1 gap-5', !channelMode && 'lg:grid-cols-[1fr_360px]')}>
            {/* ── 좌: 최근 게시물 / 최근 댓글 (탭) + 즐겨찾기 보드 필터 ── */}
            <section className="rounded-xl border border-border bg-card overflow-hidden">
              <header className="flex items-center gap-1 px-1.5 py-1.5 border-b border-border">
                <LeftMainTabButton
                  active={leftTab === 'posts'}
                  icon={FileText}
                  label={t`Recent posts`}
                  count={recentPosts.length}
                  onClick={() => setLeftTab('posts')}
                />
                <LeftMainTabButton
                  active={leftTab === 'comments'}
                  icon={MessageSquare}
                  label={t`Recent comments`}
                  count={recentComments.length}
                  onClick={() => setLeftTab('comments')}
                />
                {/* 즐겨찾기 보드 필터 토글 — 채널 모드에서는 핀 없음 → 숨김 */}
                {!channelMode && (
                <button
                  type="button"
                  onClick={() => setPinnedOnly(v => !v)}
                  disabled={pinnedIds.length === 0}
                  className={cn(
                    'size-7 rounded-md flex items-center justify-center transition-colors cursor-pointer shrink-0',
                    pinnedOnly
                      ? 'text-amber-400 bg-amber-400/10 hover:bg-amber-400/15'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted',
                    pinnedIds.length === 0 && 'opacity-40 cursor-not-allowed hover:bg-transparent',
                  )}
                  title={pinnedIds.length === 0
                    ? t`No favorite boards`
                    : pinnedOnly ? t`Show all` : t`Show only favorite boards`}
                  aria-label={t`Filter favorite boards`}
                  aria-pressed={pinnedOnly}
                >
                  <Star className={cn('size-3.5', pinnedOnly && 'fill-amber-400')} />
                </button>
                )}
                {/* 좌측 탭별 limit selector */}
                <div className="ml-1 pr-1.5">
                  {leftTab === 'posts'
                    ? <LimitSelector value={postsLimit} onChange={setPostsLimit} />
                    : <LimitSelector value={commentsLimit} onChange={setCommentsLimit} />}
                </div>
              </header>

              {leftTab === 'posts' && (
                <>
                  <div className="divide-y divide-border">
                    {loading ? (
                      <SkeletonRows count={6} />
                    ) : recentPosts.length === 0 ? (
                      <EmptyRow icon={FileText} text={pinnedOnly ? t`No posts in favorite boards.` : t`No posts yet.`} />
                    ) : (
                      recentPosts.map(item => (
                        <PostRow key={item.post.id} item={item} onClick={() => goToPost(item)} />
                      ))
                    )}
                  </div>
                  {/* 더보기 — 페이지네이션 끝까지 받지 않았을 때만 노출 */}
                  {!loading && postsHasMore && recentPosts.length > 0 && (
                    <div className="border-t border-border bg-muted/20">
                      <button
                        type="button"
                        onClick={loadMorePosts}
                        disabled={postsLoadingMore}
                        className="w-full py-2.5 inline-flex items-center justify-center gap-1.5 text-meta-sm font-medium text-muted-foreground hover:text-primary transition-colors cursor-pointer disabled:opacity-60"
                      >
                        {postsLoadingMore ? (
                          <>
                            <Loader2 className="size-3.5 animate-spin" />
                            <Trans>Loading…</Trans>
                          </>
                        ) : (
                          <>
                            <Trans>Load more</Trans>
                            <ChevronDown className="size-3.5" />
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </>
              )}

              {leftTab === 'comments' && (
                <div className="divide-y divide-border">
                  {loading ? (
                    <SkeletonRows count={6} />
                  ) : recentComments.length === 0 ? (
                    <EmptyRow icon={MessageSquare} text={pinnedOnly ? t`No comments in favorite boards.` : t`No comments yet.`} />
                  ) : (
                    recentComments.map(item => (
                      <CommentRow key={item.comment.id} item={item} onClick={() => goToComment(item)} />
                    ))
                  )}
                </div>
              )}
            </section>

            {/* ── 우: 나에게 / 참여중 — 워크스페이스 전역 섹션이라 채널 모드에서는 숨김 ── */}
            {!channelMode && (
            <aside className="space-y-5">
              <section className="rounded-xl border border-border bg-card overflow-hidden">
                <header className="flex items-center gap-1 px-1.5 py-1.5 border-b border-border">
                  <RightTabButton
                    active={rightTab === 'me'}
                    icon={AtSign}
                    label={t`Mentions`}
                    count={myFeed.length}
                    onClick={() => setRightTab('me')}
                  />
                  <RightTabButton
                    active={rightTab === 'active'}
                    icon={Users}
                    label={t`Participating`}
                    count={participatingComments.length}
                    onClick={() => setRightTab('active')}
                  />
                  {/* 우측 탭별 limit selector */}
                  <div className="ml-1 pr-1.5">
                    {rightTab === 'me'
                      ? <LimitSelector value={mentionsLimit} onChange={setMentionsLimit} />
                      : <LimitSelector value={participatingLimit} onChange={setParticipatingLimit} />}
                  </div>
                </header>

                {rightTab === 'me' && (
                  <div className="divide-y divide-border">
                    {loading ? (
                      <SkeletonRows count={4} />
                    ) : myFeed.length === 0 ? (
                      <EmptyRow icon={AtSign} text={t`No new mentions or replies.`} />
                    ) : (
                      myFeed.map((it, idx) => (
                        <MyFeedRow
                          key={`${it.kind}:${idx}:${it.mergedAt}`}
                          item={it}
                          onClick={() => {
                            if (it.kind === 'post-mention') goToPost(it.data);
                            else goToComment(it.data);
                          }}
                        />
                      ))
                    )}
                  </div>
                )}

                {rightTab === 'active' && (
                  <div className="divide-y divide-border">
                    {loading ? (
                      <SkeletonRows count={4} />
                    ) : participatingComments.length === 0 ? (
                      <EmptyRow icon={Users} text={t`No comments in discussions you're participating in.`} />
                    ) : (
                      participatingComments.map(item => (
                        <CommentRow
                          key={item.comment.id}
                          item={item}
                          highlightMine
                          onClick={() => goToComment(item)}
                        />
                      ))
                    )}
                  </div>
                )}
              </section>
            </aside>
            )}
          </div>
        </div>
      </div>

      {/* 통합 알림 설정 모달 — 사용자 개인 설정 */}
      <BoardNotifyAllSettingsModal
        open={showNotifyAll}
        onClose={() => setShowNotifyAll(false)}
        workspaceId={activeWorkspaceId}
        boardIds={channelMode ? scopeBoardIds : undefined}
      />
    </div>
  );
};

// ── 보조 컴포넌트 ──────────────────────────────────────────

const RightTabButton: React.FC<{
  active: boolean;
  icon: React.ElementType;
  label: string;
  count?: number;
  onClick: () => void;
}> = ({ active, icon: Icon, label, count, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    data-state={active ? 'on' : undefined}
    className={cn(
      'flex-1 inline-flex items-center justify-center gap-1 h-8 rounded-lg text-meta-sm font-semibold transition-colors cursor-pointer px-1.5',
      active
        ? 'bg-primary/10 text-primary'
        : 'text-muted-foreground hover:bg-muted',
    )}
  >
    <Icon className="size-3.5" />
    <span className="truncate">{label}</span>
    {count !== undefined && count > 0 && (
      <span className={cn('text-meta-sm tabular-nums', active ? 'text-primary' : 'text-muted-foreground/70')}>
        {count}
      </span>
    )}
  </button>
);

// 좌측 메인 탭(게시물/댓글) — 우측 탭과 동일 UI 패턴, 의미상 분리만.
const LeftMainTabButton = RightTabButton;

/** 섹션별 표시 개수 (20/40/60) — localStorage 에 키별로 저장.
 *  값은 number. 잘못된 값 들어오면 default 로 fallback. */
const LIMIT_OPTIONS = [20, 40, 60] as const;
type LimitValue = (typeof LIMIT_OPTIONS)[number];
function useLocalStorageNumber(key: string, def: LimitValue): [LimitValue, (v: LimitValue) => void] {
  const [val, setVal] = useState<LimitValue>(() => {
    if (typeof window === 'undefined') return def;
    try {
      const raw = localStorage.getItem(key);
      const n = raw ? Number(raw) : NaN;
      return (LIMIT_OPTIONS as readonly number[]).includes(n) ? (n as LimitValue) : def;
    } catch { return def; }
  });
  const setAndStore = (v: LimitValue) => {
    setVal(v);
    try { localStorage.setItem(key, String(v)); } catch { /* ignore */ }
  };
  return [val, setAndStore];
}

const LimitSelector: React.FC<{ value: LimitValue; onChange: (v: LimitValue) => void; label?: string }> = ({ value, onChange, label }) => (
  <div className="inline-flex items-center gap-1 shrink-0">
    {label && <span className="text-meta-sm text-muted-foreground tabular-nums">{label}</span>}
    <select
      value={value}
      onChange={e => onChange(Number(e.target.value) as LimitValue)}
      className="text-meta-sm bg-transparent border border-border rounded-md px-1.5 py-0.5 text-muted-foreground hover:text-foreground hover:border-foreground/30 cursor-pointer transition-colors outline-none focus:ring-1 focus:ring-ring"
      aria-label={t`Items per page`}
    >
      {LIMIT_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
    </select>
  </div>
);

const PostRow: React.FC<{ item: DashboardPostItem; onClick: () => void }> = ({ item, onClick }) => {
  const Icon = boardIconOf(item.boardType);
  return (
    <button
      onClick={onClick}
      className="w-full text-left px-4 py-3 hover:bg-muted/40 transition-colors cursor-pointer flex gap-3"
    >
      <UserAvatar src={item.post.author.photoUrl} name={item.post.author.name} className="size-8 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 text-meta-sm text-muted-foreground mb-0.5">
          <Icon className="size-3 shrink-0" />
          <span className="truncate">{item.boardName}</span>
          <span>·</span>
          <span className="truncate">{item.post.author.name}</span>
          <span>·</span>
          <span className="shrink-0">{timeAgo(item.post.created_at)}</span>
        </div>
        <p className="text-label font-semibold text-foreground truncate mb-0.5 flex items-center gap-1.5"><span className="truncate">{item.post.title}</span><NotifDotFor postId={item.post.id} /></p>
        {item.post.plain_text && (
          <p className="text-meta text-muted-foreground line-clamp-1">{item.post.plain_text}</p>
        )}
        {item.post.tags && item.post.tags.length > 0 && (
          <div className="flex items-center gap-1 mt-1 flex-wrap">
            {item.post.tags.slice(0, 4).map(t => (
              <span key={t} className="inline-flex items-center gap-0.5 px-1.5 py-px rounded-full bg-primary/8 text-primary text-micro">
                <Hash className="size-2 opacity-70" />{t}
              </span>
            ))}
          </div>
        )}
      </div>
      {item.post.comment_count > 0 && (
        <div className="shrink-0 text-meta-sm text-muted-foreground flex items-center gap-0.5">
          <MessageSquare className="size-3" />
          {item.post.comment_count}
        </div>
      )}
    </button>
  );
};

const CommentRow: React.FC<{ item: DashboardCommentItem; onClick: () => void; highlightMine?: boolean }> = ({ item, onClick, highlightMine }) => {
  const isMine = highlightMine && (item as ParticipatingCommentItem).isMine === true;
  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full text-left px-4 py-3 transition-colors cursor-pointer flex gap-2.5',
        // 내 댓글: amber 토큰 옅은 배경 + 좌측 strip 으로 시각 구분
        isMine
          ? 'bg-[rgb(var(--badge-caution)/0.06)] hover:bg-[rgb(var(--badge-caution)/0.10)] border-l-2 border-[rgb(var(--badge-caution))]'
          : 'hover:bg-muted/40',
      )}
    >
      <UserAvatar src={item.comment.author.photoUrl} name={item.comment.author.name} className="size-7 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 text-meta-sm text-muted-foreground mb-0.5">
          <span className="font-medium text-foreground truncate">{item.comment.author.name}</span>
          {isMine && (
            <span className="text-[color:rgb(var(--badge-caution))] text-meta-sm font-semibold">
              <Trans>You</Trans>
            </span>
          )}
          <span className="shrink-0 ml-auto">{timeAgo(item.comment.created_at)}</span>
        </div>
        <p className="text-meta text-foreground/90 line-clamp-2 leading-relaxed">{item.comment.text}</p>
        <p className="text-meta-sm text-muted-foreground truncate mt-1">
          ↳ <span className="text-foreground/70">{item.postTitle}</span> · {item.boardName}
        </p>
      </div>
    </button>
  );
};

const MyFeedRow: React.FC<{ item: MyFeedItem; onClick: () => void }> = ({ item, onClick }) => {
  const kindMeta = (() => {
    switch (item.kind) {
      case 'post-mention':    return { icon: AtSign, label: t`Post mention`,    tone: 'text-primary bg-primary/10' };
      case 'comment-mention': return { icon: AtSign, label: t`Comment mention`, tone: 'text-primary bg-primary/10' };
      case 'reply-to-mine':   return { icon: Reply,  label: t`Reply`,           tone: 'text-badge-caution bg-badge-caution-bg' };
    }
  })();
  const KIcon = kindMeta.icon;

  if (item.kind === 'post-mention') {
    const p = item.data;
    return (
      <button
        onClick={onClick}
        className="w-full text-left px-4 py-3 hover:bg-muted/40 transition-colors cursor-pointer"
      >
        <div className="flex items-center gap-1.5 mb-1">
          <span className={cn('inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-meta-sm font-medium', kindMeta.tone)}>
            <KIcon className="size-2.5" />{kindMeta.label}
          </span>
          <span className="text-meta-sm text-muted-foreground truncate flex-1">{p.boardName}</span>
          <span className="text-meta-sm text-muted-foreground shrink-0">{timeAgo(p.post.created_at)}</span>
        </div>
        <p className="text-label font-semibold text-foreground truncate flex items-center gap-1.5"><span className="truncate">{p.post.title}</span><NotifDotFor postId={p.post.id} /></p>
        <p className="text-meta-sm text-muted-foreground truncate mt-0.5">{p.post.author.name}</p>
      </button>
    );
  }

  const c = item.data;
  return (
    <button
      onClick={onClick}
      className="w-full text-left px-4 py-3 hover:bg-muted/40 transition-colors cursor-pointer"
    >
      <div className="flex items-center gap-1.5 mb-1">
        <span className={cn('inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-meta-sm font-medium', kindMeta.tone)}>
          <KIcon className="size-2.5" />{kindMeta.label}
        </span>
        <span className="text-meta-sm text-muted-foreground truncate flex-1">{c.comment.author.name}</span>
        <span className="text-meta-sm text-muted-foreground shrink-0">{timeAgo(c.comment.created_at)}</span>
      </div>
      <p className="text-meta text-foreground/90 line-clamp-2 leading-relaxed">{c.comment.text}</p>
      <p className="text-meta-sm text-muted-foreground truncate mt-1">↳ {c.postTitle} · {c.boardName}</p>
    </button>
  );
};

export const SkeletonRows: React.FC<{ count: number }> = ({ count }) => (
  <>
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="px-4 py-3 animate-pulse flex gap-3">
        <div className="size-8 rounded-full bg-muted shrink-0" />
        <div className="flex-1 space-y-1.5">
          <div className="h-3 w-1/3 bg-muted rounded" />
          <div className="h-3.5 w-3/4 bg-muted rounded" />
          <div className="h-3 w-1/2 bg-muted rounded" />
        </div>
      </div>
    ))}
  </>
);

const EmptyRow: React.FC<{ icon: React.ElementType; text: string }> = ({ icon: Icon, text }) => (
  <div className="flex flex-col items-center justify-center py-10 text-muted-foreground gap-2">
    <Icon className="size-8 opacity-30" />
    <p className="text-meta-sm text-center px-4">{text}</p>
  </div>
);

export default BoardDashboardPage;
